// step-setup-alerts.js
//
// First of the two enterprise-onboarding steps that run once an
// enterprise-related sensor finishes setup.
//
// Deliberately read-only: no toggles, no save. It exists to answer "why are you
// asking me about contacts next?" by showing the alerts that will watch this
// sensor — including the ones the enterprise set — so the contacts step that
// follows has an obvious purpose. Anything assignable belongs on the manage
// sensor page, not here.
//
// Condensed to one phone screen: the reading types are summarised as a row of
// icon tiles rather than the manage-sensor page's one-card-per-type list, split
// into what the organization manages and what belongs to this account. Nothing
// here is actionable, so per-alert names and thresholds are detail this step
// doesn't owe the reader — they live on the tile's tooltip and on the Alerts
// page. Grouping still comes from util/alert-groups so the two surfaces agree on
// which alerts apply to this sensor. Only the tile names differ: here the probe
// is named after the probe that was just plugged in (util/probe-display), and
// the sensor's own reading is called the ambient one to keep the two apart.

import getAlerts from '../../scripts/get-alerts'
import icon from '../../util/icon'
import { onboardingReadingTypeMeta, probeSelectionFromSensor } from '../../util/probe-display'
import {
  groupAlertsByType,
  isManagedAlert,
  isPausedAlert,
} from '../../util/alert-groups'

export class StepSetupAlerts {

  constructor (onboarding) {
    this.onboarding = onboarding;
  }

  id = "step_setup_alerts";
  title = "Your Alerts";

  alerts = [];
  sensor = null;
  isLoading = false;
  loadFailed = false;

  template = `
  <div class="onboarding-step" data-for="step_setup_alerts">
    <div class="onboarding-alerts-review-stage"></div>
  </div>
  `;

  onBack = () => {
    // Setup is already committed by the time this step runs — there is nothing
    // behind it to go back to.
  }

  onActivate = () => {
    this.onboarding.hideBackButton();
    this.load();
  }

  bindActions = () => {

    this.$root = $(this.onboarding.container).find('[data-for="step_setup_alerts"]');
    this.$stage = this.$root.find('.onboarding-alerts-review-stage');

    this.$root.on('click', '[data-for="alerts_review_continue"]', () => {
      this.goToNextStep();
    });

    this.$root.on('click', '[data-for="alerts_review_retry"]', () => {
      this.load();
    });

  }

  getSensorId = () => {
    return this.onboarding.sensor && this.onboarding.sensor.sid
      ? this.onboarding.sensor.sid
      : null;
  }

  /**
   * The sensor record is needed to filter alerts by what this hardware can
   * actually report (probe/light/motion/power). Setup only knows the sid and mac,
   * so fetch the real record — but a failure here is not fatal: without it the
   * grouping keeps every alert rather than hiding any.
   */
  loadSensor = async () => {

    const sensorId = this.getSensorId();

    if (!sensorId) {
      return null;
    }

    try {

      const r = await window.apiRequest('get', `/sensor/${encodeURIComponent(sensorId)}`);

      // the endpoint returns the sensor record as data directly
      if (r && r.type === 'success' && r.data) {
        return r.data;
      }

    } catch (e) {
      console.error(e);
    }

    return null;

  }

  load = async () => {

    this.isLoading = true;
    this.loadFailed = false;
    this.render();

    const [sensor, alerts] = await Promise.all([
      this.loadSensor(),
      // don't serve a stale cache — enterprise alerts may have just synced
      getAlerts(true, false).catch(e => {
        console.error(e);
        this.loadFailed = true;
        return [];
      }),
    ]);

    this.sensor = sensor;
    this.alerts = Array.isArray(alerts) ? alerts : [];
    this.isLoading = false;

    this.render();

  }

  escapeHtml (value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, char => {
      switch (char) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case '\'': return '&#39;';
        default: return char;
      }
    });
  }

  /**
   * Re-split the reading-type groups into the organization's alerts and the
   * account's own, keeping each side's grouping and order. A reading type with
   * alerts on both sides appears in both rows, which is the honest reading of
   * "two things are watching your temperature and only one is yours".
   *
   * @returns {{managed: Array, own: Array}} groups shaped like groupAlertsByType's
   */
  splitByOwner (groups) {

    const managed = [];
    const own = [];

    groups.forEach(group => {

      const mine = group.alerts.filter(alert => !isManagedAlert(alert));
      const theirs = group.alerts.filter(isManagedAlert);

      if (theirs.length) {
        managed.push({ ...group, alerts: theirs });
      }

      if (mine.length) {
        own.push({ ...group, alerts: mine });
      }

    });

    return { managed, own };

  }

  countAlerts (groups) {
    return groups.reduce((sum, group) => sum + group.alerts.length, 0);
  }

  /**
   * Which probe the person picked, so the probe tile can be named after it. The
   * probe step's answer comes first; a review reached without it (a reload)
   * falls back to what the sensor record says was stored.
   */
  probeSelection () {
    return this.onboarding.probe_selection || probeSelectionFromSensor(this.sensor);
  }

  /**
   * One reading type as an icon tile, badged with how many alerts it carries.
   * Every tile gets the badge, including the ones carrying a single alert: an
   * unbadged tile reads as "no count available" rather than as one, and the
   * reader shouldn't have to know the rule to count the row.
   */
  renderTypeTile (group) {

    const count = group.alerts.length;

    // the names are the detail the tile drops; keep them a hover away
    const names = group.alerts
      .map(alert => `${alert.alert_name}${isPausedAlert(alert) ? ' (paused)' : ''}`)
      .join(', ');

    const allPaused = group.alerts.every(isPausedAlert);

    return `
    <div
      class="onboarding-alert-type${allPaused ? ' is-paused' : ''}"
      data-type="${group.key}"
      title="${this.escapeHtml(names)}"
    >
      <span class="onboarding-alert-type__icon">${icon(group.icon)}</span>
      <span class="onboarding-alert-type__label">${this.escapeHtml(group.title)}</span>
      <span class="onboarding-alert-type__count">${count}</span>
    </div>
    `;

  }

  /**
   * A labelled row of tiles. The label is dropped when there is only one section
   * to show — "Your Alerts" above the only row repeats the step's own heading.
   */
  renderSection (label, groups, withLabel, modifier = '', labelTitle = '') {

    const head = withLabel
      ? `
        <div class="onboarding-alerts-review__section-head">
          <span
            class="onboarding-alerts-review__section-label"
            ${labelTitle ? `title="${this.escapeHtml(labelTitle)}"` : ''}
          >${label}</span>
        </div>
      `
      : '';

    return `
    <div class="onboarding-alerts-review__section${modifier ? ` onboarding-alerts-review__section--${modifier}` : ''}">
      ${head}
      <div class="onboarding-alert-types">
        ${groups.map(group => this.renderTypeTile(group)).join('')}
      </div>
    </div>
    `;

  }

  renderCard (body, footer) {

    this.$stage.html(`
      <div class="onboarding-alerts-review">
        <div class="onboarding-alerts-review__card">
          ${body}
          ${footer}
        </div>
      </div>
    `);

  }

  continueFooter (label = 'Continue') {
    return `
      <div class="onboarding-alerts-review__actions">
        <button type="button" class="button button--medium" data-for="alerts_review_continue">
          ${label}
        </button>
      </div>
    `;
  }

  render () {

    if (!this.$stage || this.$stage.length === 0) {
      return;
    }

    if (this.isLoading) {

      this.renderCard(`
        <div class="onboarding-alerts-review__loading">
          <div class="onboarding-alerts-review__loading-text">Loading your alerts...</div>
        </div>
      `, '');

      return;

    }

    if (this.loadFailed) {

      this.renderCard(`
        <div class="onboarding-alerts-review__title">We couldn't load your alerts</div>
        <p class="onboarding-alerts-review__intro">
          Your sensor is set up and monitoring. You can review your alerts any time from the Alerts page.
        </p>
      `, `
        <div class="onboarding-alerts-review__actions">
          <button type="button" class="button button--grey-outline" data-for="alerts_review_retry">
            Try Again
          </button>
          <button type="button" class="button button--medium" data-for="alerts_review_continue">
            Continue
          </button>
        </div>
      `);

      return;

    }

    const groups = groupAlertsByType(
      this.sensor,
      this.alerts,
      onboardingReadingTypeMeta(this.probeSelection())
    );

    if (groups.length === 0) {

      // Nothing is watching this sensor yet. Say so plainly rather than showing
      // an empty frame — and still lead into contacts, since the contacts are
      // what make any alert added later actually reach someone.
      this.renderCard(`
        <div class="onboarding-alerts-review__hero">${icon('bell')}</div>
        <div class="onboarding-alerts-review__title">No alerts are watching this sensor yet</div>
        <p class="onboarding-alerts-review__intro">
          You can add them from the Alerts page once setup is done.
        </p>
      `, this.continueFooter());

      return;

    }

    const total = this.countAlerts(groups);
    const { managed, own } = this.splitByOwner(groups);

    // "Your Alerts" over the only row repeats the step's own heading, so the
    // account's row is only labelled when there is a managed row to tell it
    // from. The managed row keeps its label either way — that these were set by
    // someone else is the one thing this step can't leave the reader to infer.
    this.renderCard(`
      <div class="onboarding-alerts-review__title">
        Alerts watching this sensor
      </div>

      <p class="onboarding-alerts-review__intro">
        You'll be notified the moment a reading goes out of range.
      </p>

      ${managed.length
        ? this.renderSection(
            'Predefined Alerts',
            managed,
            true,
            'managed',
            'Set by your organization and managed for you'
          )
        : ''}

      ${own.length
        ? this.renderSection('Your Alerts', own, managed.length > 0)
        : ''}
    `, this.continueFooter());

  }

  goToNextStep = () => {
    this.onboarding.goToStep('step_setup_contacts');
  }

}
